import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Tooltip } from 'ui/tooltip';
import { AttributionMode, Experiment, WinningCriteriaType } from 'modules/domains/campaign';
import { DEFAULT_CONVERSIONS_PERIOD_HOUR } from 'modules/campaigns/lib/constants';
import { InputField } from 'lib/iterable-hook-form/FormField';
import { ConversionSelect } from 'modules/campaigns/lib/components/ConversionSelect/ConversionSelect';
import { useSelector } from 'react-redux';
import {
  selectIsJourneyGoalsFeatureFlagOn,
  selectIsMissionControlHomepageAndBetaFeatureFlagOn,
} from 'core/userContext';
import { CampaignIdBadge } from 'modules/campaigns/lib/components/CampaignIdBadge';
import {
  useInheritedConversionInfo,
  InheritedConversionSource,
} from 'modules/campaigns/lib/hooks/useInheritedConversionInfo';
import { useCampaignContext } from 'modules/campaigns/editor/context';
import { ErrorText } from '../../../styles';
import { CampaignEditorFormState } from '../../../types';
import {
  AttributeWrapper,
  CodeBlock,
  InputWrapper,
  LastTouchLabelWrapper,
} from './EditableConversion.styles';
import { AttributionRow } from './AttributionRow';
import { RadioGroupField } from 'lib/iterable-hook-form';
import { ErrorText as FormFieldErrorText } from 'ui/formField';
import { ErrorWrapper } from './EditableConversion.styles';
const validationKey = 'conversions';
const attributionModeKey = `${validationKey}.mode`;
const attributionPeriodHoursKey = `${validationKey}.periodHours`;

export const EditableConversion = () => {
  const { campaign } = useCampaignContext();

  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isMissionControlAndBetaEnabled = useSelector(
    selectIsMissionControlHomepageAndBetaFeatureFlagOn,
  );

  const {
    journeyEventName,
    projectEventNames,
    isAttributionModesDisabled,
    attributionModesDisabledTooltipContent,
  } = useInheritedConversionInfo({
    projectGoals: [],
    journeyGoals: campaign?.journey?.goals || [],
    source: InheritedConversionSource.CampaignEditor,
  }); // TODO: Add project goals events as first first param once the backend is ready.

  const experiments = useWatch({ name: 'experiments' });
  const conversionExperiments = experiments?.experiments;

  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<CampaignEditorFormState>();

  useEffect(() => {
    watch((_, { name }) => {
      if (name === attributionModeKey) {
        setValue(
          attributionPeriodHoursKey as keyof CampaignEditorFormState,
          DEFAULT_CONVERSIONS_PERIOD_HOUR.toString(),
        );
        trigger(validationKey);
      } else if (name?.startsWith(validationKey)) {
        trigger(validationKey);
      }
    });
  }, [watch, trigger, setValue]);

  const attributionMode = useWatch({
    name: attributionModeKey,
  });

  const conversions = useWatch({
    name: 'conversions.events',
  });
  const { conversions: conversionsError } = errors;

  const hasExperimentWithConversions =
    conversionExperiments?.filter(
      (x: Experiment) => x?.winningCriteria?.type === WinningCriteriaType.ConversionEvent,
    )?.length > 0;

  const inheritedAttributionTooltipContent = isAttributionModesDisabled
    ? attributionModesDisabledTooltipContent
    : undefined;

  return (
    <>
      <Tooltip
        isEnabled={hasExperimentWithConversions}
        tooltipContent={
          'Conversion events are tied to your experiment. Visit the experiment page to edit.'
        }
      >
        <ConversionSelect
          id="campaign"
          isDisabled={hasExperimentWithConversions}
          journeyEventName={journeyEventName}
          projectEventNames={projectEventNames}
        />
      </Tooltip>

      {conversions?.length >= 1 &&
        (isJourneyGoalsEnabled || isMissionControlAndBetaEnabled ? (
          <AttributeWrapper>
            <RadioGroupField
              name={attributionModeKey}
              isReadOnly={isAttributionModesDisabled}
              options={[
                {
                  label: (
                    <LastTouchLabelWrapper>
                      Last touch attribution within{' '}
                      {attributionMode === AttributionMode.LastMessaged && (
                        <InputWrapper>
                          <InputField
                            isDisabled={isAttributionModesDisabled}
                            name={attributionPeriodHoursKey}
                            type="number"
                            placeholder={DEFAULT_CONVERSIONS_PERIOD_HOUR.toString()}
                            Suffix="hours"
                          />
                        </InputWrapper>
                      )}
                      {conversionsError?.message && (
                        <ErrorWrapper>
                          <FormFieldErrorText>{conversionsError?.message}</FormFieldErrorText>
                        </ErrorWrapper>
                      )}
                    </LastTouchLabelWrapper>
                  ),
                  value: AttributionMode.LastMessaged,
                  id: AttributionMode.LastMessaged,
                  tooltipContent: inheritedAttributionTooltipContent,
                  isTooltipEnabled: isAttributionModesDisabled,
                  isDisabled: isAttributionModesDisabled,
                },
                {
                  label: (
                    <>
                      {`Attribute only conversion events with explicitly set `}
                      <CampaignIdBadge isDisabled={isAttributionModesDisabled} /> field
                    </>
                  ),
                  value: AttributionMode.ExplicitlySet,
                  id: AttributionMode.ExplicitlySet,
                  tooltipContent: inheritedAttributionTooltipContent,
                  isTooltipEnabled: isAttributionModesDisabled,
                  isDisabled: isAttributionModesDisabled,
                },
              ]}
            />
          </AttributeWrapper>
        ) : (
          /**
           * Once the JourneyGoals feature flag is GA'd, remove the code below and the components that we don't need since we have inlined them.
           */
          <AttributeWrapper>
            <AttributionRow
              name={attributionModeKey}
              attributionMode={AttributionMode.ExplicitlySet}
              text={
                <>
                  Attribute only conversion events with explicitly set{' '}
                  {<CodeBlock>campaignId</CodeBlock>} field
                </>
              }
              tooltip="To count as a conversion, an incoming event must have a campaignId field that matches this campaign's ID."
            />

            <AttributionRow
              name={attributionModeKey}
              attributionMode={AttributionMode.LastMessaged}
              text="Attribute any conversion events received within set period"
              tooltip={
                'To count as a conversion, an incoming event must arrive within the specified hours of when the campaign is sent. If multiple campaigns use the same conversion event, the conversion is assigned to the last one sent.'
              }
            >
              {attributionMode === AttributionMode.LastMessaged && (
                <>
                  <Tooltip
                    isEnabled={isAttributionModesDisabled}
                    tooltipContent="Attribution is determined by the parent Journey's Conversion settings"
                  >
                    <InputWrapper>
                      <InputField
                        name={attributionPeriodHoursKey}
                        type="number"
                        placeholder={DEFAULT_CONVERSIONS_PERIOD_HOUR.toString()}
                        Suffix="hours"
                      />
                    </InputWrapper>
                  </Tooltip>
                  {conversionsError?.message && <ErrorText>{conversionsError.message}</ErrorText>}
                </>
              )}
            </AttributionRow>
          </AttributeWrapper>
        ))}
    </>
  );
};
